const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

export const DEFAULT_NOTE_THEME_LIGHTNESS = Object.freeze({
  lightBackground: 80,
  lightText: 28,
  darkBackground: 20,
  darkText: 84,
});

function normalizeHex(value) {
  return typeof value === 'string' && HEX_COLOR_PATTERN.test(value.trim())
    ? value.trim().toLowerCase()
    : null;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function normalizeLightness(value, fallback) {
  const number = Number(value);
  return Number.isFinite(number) ? clamp(number, 0, 100) : fallback;
}

export function normalizeNoteThemeLightness(value = {}) {
  return {
    lightBackground: normalizeLightness(value.lightBackground, DEFAULT_NOTE_THEME_LIGHTNESS.lightBackground),
    lightText: normalizeLightness(value.lightText, DEFAULT_NOTE_THEME_LIGHTNESS.lightText),
    darkBackground: normalizeLightness(value.darkBackground, DEFAULT_NOTE_THEME_LIGHTNESS.darkBackground),
    darkText: normalizeLightness(value.darkText, DEFAULT_NOTE_THEME_LIGHTNESS.darkText),
  };
}

function hexToHsl(hex) {
  const value = Number.parseInt(hex.slice(1), 16);
  const red = ((value >> 16) & 255) / 255;
  const green = ((value >> 8) & 255) / 255;
  const blue = (value & 255) / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  let hue = 0;
  if (delta) {
    if (max === red) hue = ((green - blue) / delta) % 6;
    else if (max === green) hue = ((blue - red) / delta) + 2;
    else hue = ((red - green) / delta) + 4;
    hue = ((hue * 60) + 360) % 360;
  }
  const lightness = (max + min) / 2;
  const saturation = delta ? delta / (1 - Math.abs((2 * lightness) - 1)) : 0;
  return { hue, saturation, lightness };
}

function hslToHex(hue, saturation, lightness) {
  const chroma = (1 - Math.abs((2 * lightness) - 1)) * saturation;
  const section = hue / 60;
  const x = chroma * (1 - Math.abs((section % 2) - 1));
  let components = [0, 0, 0];
  if (section < 1) components = [chroma, x, 0];
  else if (section < 2) components = [x, chroma, 0];
  else if (section < 3) components = [0, chroma, x];
  else if (section < 4) components = [0, x, chroma];
  else if (section < 5) components = [x, 0, chroma];
  else components = [chroma, 0, x];
  const match = lightness - (chroma / 2);
  return `#${components.map((component) => clamp(Math.round((component + match) * 255), 0, 255).toString(16).padStart(2, '0')).join('')}`;
}

export function adjustNoteThemeLightness(hex, lightness) {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  const hsl = hexToHsl(normalized);
  return hslToHex(hsl.hue, hsl.saturation, clamp(Number(lightness) / 100, 0, 1));
}

function getPaletteTheme(note, lightness) {
  const source = note && typeof note === 'object' ? note : {};
  const themeColorTop = normalizeHex(source.themeColorTop);
  const themeColorBottom = normalizeHex(source.themeColorBottom);
  if (!themeColorTop || !themeColorBottom) return null;

  const values = normalizeNoteThemeLightness(lightness);
  const topLight = adjustNoteThemeLightness(themeColorTop, values.lightBackground);
  const topDark = adjustNoteThemeLightness(themeColorTop, values.darkBackground);
  const bottomLight = adjustNoteThemeLightness(themeColorBottom, values.lightBackground);
  const bottomDark = adjustNoteThemeLightness(themeColorBottom, values.darkBackground);
  const lightText = adjustNoteThemeLightness(themeColorBottom, values.lightText);
  const darkText = adjustNoteThemeLightness(themeColorBottom, values.darkText);
  return {
    themeColorTop,
    themeColorBottom,
    themeColorTopLight: topLight,
    themeColorTopDark: topDark,
    themeColorBottomLight: bottomLight,
    themeColorBottomDark: bottomDark,
    themeColorLight: bottomLight,
    themeColorDark: bottomDark,
    themeColorLightText: lightText,
    themeColorDarkText: darkText,
  };
}

export function normalizeNoteTheme(note = {}, lightness = DEFAULT_NOTE_THEME_LIGHTNESS) {
  const palette = getPaletteTheme(note, lightness);
  if (palette) return palette;
  const source = note && typeof note === 'object' ? note : {};
  return {
    themeColorLight: normalizeHex(source.themeColorLight),
    themeColorDark: normalizeHex(source.themeColorDark),
  };
}

export function getNoteThemeStyle(note = {}, lightness = DEFAULT_NOTE_THEME_LIGHTNESS) {
  const normalized = normalizeNoteTheme(note, lightness);
  if (normalized.themeColorTop && normalized.themeColorBottom) {
    return {
      // The cover edge gradient uses the extracted top color directly. It is
      // an image-derived accent and must not be altered by the temporary
      // lightness controls for page backgrounds or text.
      '--note-theme-top': normalized.themeColorTop,
      '--note-theme-bottom': normalized.themeColorBottom,
      '--note-theme-top-light': normalized.themeColorTopLight,
      '--note-theme-top-dark': normalized.themeColorTopDark,
      '--note-theme-bottom-light': normalized.themeColorBottomLight,
      '--note-theme-bottom-dark': normalized.themeColorBottomDark,
      '--note-theme-light': normalized.themeColorLight,
      '--note-theme-dark': normalized.themeColorDark,
      '--note-theme-light-text': normalized.themeColorLightText,
      '--note-theme-dark-text': normalized.themeColorDarkText,
    };
  }
  if (!normalized.themeColorLight || !normalized.themeColorDark) return undefined;
  return {
    '--note-theme-light': normalized.themeColorLight,
    '--note-theme-dark': normalized.themeColorDark,
  };
}

export function hasNoteTheme(note = {}, lightness) {
  return Boolean(getNoteThemeStyle(note, lightness));
}

export function getPracticeNoteMetadata(note) {
  if (!note || typeof note !== 'object') return null;
  const normalized = normalizeNoteTheme(note);
  return {
    noteId: note.noteId ?? note.id ?? null,
    ...(note.difficulty != null ? { difficulty: note.difficulty } : {}),
    name: note.noteTitle || note.title || note.name || '',
    coverUrl: typeof note.coverUrl === 'string' ? note.coverUrl : '',
    ...(normalized.themeColorTop && normalized.themeColorBottom
      ? { themeColorTop: normalized.themeColorTop, themeColorBottom: normalized.themeColorBottom }
      : { themeColorLight: normalized.themeColorLight, themeColorDark: normalized.themeColorDark }),
  };
}
